import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableSortLabel } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useSearchParams } from "react-router-dom";
import FilterLateConversion from "./LoadedAccordionsFilter/FilterLoadedAccordions";
import dayjs from "dayjs";

interface Props {
  rows: any[];
  selecteDate: any;
  total: number;
}

const tableHeadings = {
  keyword_id: "Keyword ID",
  keyword_name: "Keyword Name",
  matchtype: "Match Type",
  traffic_source: "Traffic Source",
  adgroup_name: "Ad Group Name",
  adgroup_id: "Ad Group ID",
  campaign_name: "Campaign Name",
  campaign_id: "Campaign ID",
  ad_platform_account_name: "Ad Account Name",
  ad_platform_click_id: "AD Platform Click ID",
  ad_platform_account_id: "Ad Platform Account ID",
  user_unique_id: "User Unique ID",
  click_date: "Click Date",
  conversion_date: "Conversion Date",
  brand_id: "Brand ID",
  brand_name: "Brand Name",
};

const MissingPlatformID: React.FC<Props> = React.memo(
  ({ rows, selecteDate, total }: Props) => {
    const [expanded, setExpanded] = React.useState<string | false>(false);
    const [searchKeyWord, SetSearchKeyWord] = useState("");

    const percentage = rows?.length
      ? ((rows?.length / total) * 100)?.toFixed(2)
      : 0;
    const [sortField, setSortField] = useState("keyword_id") as any;
    const [sortOrder, setSortOrder] = useState("asc") as any;

    const [searchParams, setSearchParams] = useSearchParams();

    const searchOptions = rows?.reduce(
      (acc: Record<string, any[]>, site: any) => {
        const key = site?.keyword_name; // group key
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(site); // push the full object
        return acc;
      },
      {}
    ) as any;

    //filters state
    const [selectedTrafficSource, setSelectedTrafficSource] = useState(
      []
    ) as any;
    const [selectedMatchType, setSelectedMatchType] = useState([]) as any;
    const [selectedCampaign, setSelectedCampaign] = useState([]) as any;
    const [selectedAdGroup, setSelectedAdGroup] = useState([]) as any;

    const handleChange =
      (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
      };

    const headCells = Object.entries(tableHeadings);

    // Function to handle header clicks
    const handleSortClick = (field: any) => {
      if (sortField === field) {
        // Toggle order if same column
        setSortOrder((prev: any) => (prev === "asc" ? "desc" : "asc"));
      } else {
        // Set new column & reset order to asc
        setSortField(field);
        setSortOrder("asc");
      }
    };

    const filteredAndSortedData = useMemo(() => {
      if (rows?.length) {
        let result = [...rows];

        // Filter by Traffic Source
        if (selectedTrafficSource?.length > 0) {
          result = result.filter((item) =>
            selectedTrafficSource?.includes(item?.traffic_source)
          );
        }

        // Filter by MatchType
        if (selectedMatchType?.length > 0) {
          result = result?.filter((item: any) =>
            selectedMatchType?.includes(item?.matchtype)
          );
        }

        // Filter by Campaign
        if (selectedCampaign?.length > 0) {
          result = result?.filter((item) =>
            selectedCampaign?.includes(item?.campaign_name)
          );
        }

        // Filter by AdGroup
        if (selectedAdGroup?.length > 0) {
          result = result?.filter((item) =>
            selectedAdGroup?.includes(item?.adgroup_name)
          );
        }

        if (searchKeyWord?.length > 0) {
          result = result?.filter(
            (item) => item?.keyword_name === searchKeyWord
          );
        }

        // Sort
        result?.sort((a, b) => {
          if (a[sortField] < b[sortField]) return sortOrder === "asc" ? -1 : 1;
          if (a[sortField] > b[sortField]) return sortOrder === "asc" ? 1 : -1;
          return 0;
        });

        return result;
      } else {
        return [];
      }
    }, [
      selectedTrafficSource,
      selectedMatchType,
      selectedCampaign,
      selectedAdGroup,
      sortField,
      sortOrder,
      rows,
      searchKeyWord,
    ]);

    const handelSelectedKeyWord = (_: any, newValue: any) => {
      SetSearchKeyWord(newValue);
    };

    useEffect(() => {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.delete("filters");
      // Update the URL
      setSearchParams(updatedSearchParams);
      //eslint-disable-next-line
    }, [
      selectedTrafficSource,
      selectedMatchType,
      selectedCampaign,
      selectedAdGroup,
    ]);

    useEffect(() => {
      if (selecteDate) {
        setSelectedTrafficSource([]);
        setSelectedMatchType([]);
        setSelectedCampaign([]);
        setSelectedAdGroup([]);
        SetSearchKeyWord("");
      }
      //eslint-disable-next-line
    }, [selecteDate]);

    return (
      <>
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleChange("panel1")}
          sx={{ margin: "0 !important" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography
              component="span"
              sx={{
                width: "33%",
                flexShrink: 0,
                fontSize: "24px !important",
                fontWeight: 400,
              }}
            >
              Missing Platform ID
              <Typography fontSize={14} fontWeight={400} color="text.secondary">
                Failed due to this reason: Failed due to this reason:{" "}
                {rows?.length > 0 ? rows?.length : 0}{" "}
                {`(${percentage}% of ${total} FTD’s)`}
              </Typography>
            </Typography>
            <Typography
              component="span"
              sx={{
                color: "text.secondary",
                fontSize: 12,
                width: "50%",
                display: "flex",
                alignItems: "center",
              }}
            >
              See how many conversions were reported at the aggregate level, how
              many at the player level, and the gap between them by brand.
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "8px 8px 8px 0px !Important" }}>
            {expanded && rows?.length > 0 ? (
              <>
                <Grid container spacing={2} margin={"16px"}>
                  <Grid size={3}>
                    <Autocomplete
                      value={searchKeyWord}
                      onChange={handelSelectedKeyWord}
                      sx={{ width: "100%" }}
                      options={Object.keys(searchOptions)}
                      getOptionLabel={(option: any) => option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Selected Key Word"
                          color="secondary"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            readOnly: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ marginLeft: "12px" }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={9}>
                    <FilterLateConversion
                      data={rows}
                      setSelectedAdGroup={setSelectedAdGroup}
                      setSelectedCampaign={setSelectedCampaign}
                      setSelectedMatchType={setSelectedMatchType}
                      setSelectedTrafficSource={setSelectedTrafficSource}
                      selectedTrafficSource={selectedTrafficSource}
                      selectedMatchType={selectedMatchType}
                      selectedCampaign={selectedCampaign}
                      selectedAdGroup={selectedAdGroup}
                    />
                  </Grid>
                </Grid>
                <TableContainer
                  className="PageScantablewrap"
                  sx={{ maxHeight: 588 }}
                >
                  {rows?.length > 0 ? (
                    <Table
                      aria-label="collapsible table"
                      size="small"
                      stickyHeader
                    >
                      <TableHead>
                        <TableRow>
                          {headCells.map(([key, value], index) => (
                            <TableCell key={index} align="left">
                              <TableSortLabel
                                active={sortField === key}
                                direction={
                                  sortField === key ? sortOrder : "asc"
                                }
                                onClick={() => handleSortClick(key)}
                              >
                                {value}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAndSortedData?.map((row, rowIndex) => (
                          <React.Fragment key={rowIndex}>
                            <TableRow
                              sx={{
                                "& > *": {
                                  borderBottom:
                                    "1px solid rgba(224, 224, 224, 1) !important",
                                },
                              }}
                            >
                              <>
                                <TableCell key={1 + Math.random()} align="left">
                                  {row?.keyword_id}
                                </TableCell>
                                <TableCell key={2 + Math.random()} align="left">
                                  {row?.keyword_name}
                                </TableCell>
                                <TableCell key={3 + Math.random()} align="left">
                                  {row?.matchtype}
                                </TableCell>
                                <TableCell key={4 + Math.random()} align="left">
                                  {row?.traffic_source}
                                </TableCell>
                                <TableCell key={5 + Math.random()} align="left">
                                  {row?.adgroup_name}
                                </TableCell>
                                <TableCell key={6 + Math.random()} align="left">
                                  {row?.adgroup_id}
                                </TableCell>
                                <TableCell key={1 + Math.random()} align="left">
                                  {row?.campaign_name}
                                </TableCell>
                                <TableCell key={7 + Math.random()} align="left">
                                  {row?.campaign_id}
                                </TableCell>
                                <TableCell key={8 + Math.random()} align="left">
                                  {row?.ad_platform_account_name}
                                </TableCell>
                                <TableCell key={9 + Math.random()} align="left">
                                  {row?.ad_platform_click_id}
                                </TableCell>
                                <TableCell
                                  key={10 + Math.random()}
                                  align="left"
                                >
                                  {row?.ad_platform_account_id}
                                </TableCell>
                                <TableCell
                                  key={11 + Math.random()}
                                  align="left"
                                >
                                  {row?.user_unique_id}
                                </TableCell>
                                <TableCell
                                  key={12 + Math.random()}
                                  align="left"
                                >
                                  {dayjs(row?.click_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell
                                  key={13 + Math.random()}
                                  align="left"
                                >
                                  {dayjs(row?.conversion_date).format(
                                    "DD-MM-YYYY"
                                  )}
                                </TableCell>
                                <TableCell
                                  key={14 + Math.random()}
                                  align="left"
                                >
                                  {row?.brand_id}
                                </TableCell>
                                <TableCell
                                  key={15 + Math.random()}
                                  align="left"
                                >
                                  {row?.brand_name}
                                </TableCell>
                              </>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Alert
                      sx={{ color: "#ED6C02", mb: 1 }}
                      severity="warning"
                      icon={
                        <InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />
                      }
                    >
                      NO DATA FOR Missing platform ID
                    </Alert>
                  )}
                </TableContainer>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "100px", // gives some vertical space
                  width: "100%",
                }}
              >
                <Typography
                  fontSize={20}
                  fontWeight={500}
                  color="text.secondary"
                >
                  NO DATA FOR Missing platform ID
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </>
    );
  }
);

export default MissingPlatformID;
